import React, { useState, useRef } from 'react';
import { Camera, RefreshCw, AlertCircle, CheckCircle, Upload } from 'lucide-react';
import { BaseScanner, ScannerContentProps } from '../BaseScanner/BaseScanner';
import { API_BASE_URL } from '@/config/constants';
import './BusinessCardScanner.css';

interface BusinessCardScannerProps {
  onScanSuccess: (contactData: any) => void;
  onClose: () => void;
  isOpen: boolean;
  getAccessToken: () => Promise<string>;
  mode?: 'wallet' | 'card';
}

export const BusinessCardScanner: React.FC<BusinessCardScannerProps> = ({ 
  onScanSuccess, 
  onClose, 
  isOpen,
  getAccessToken,
  mode: scannerMode = 'wallet'
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [errorSuggestion, setErrorSuggestion] = useState<string | null>(null);
  const [isRetryableError, setIsRetryableError] = useState<boolean>(false);
  const [scanResult, setScanResult] = useState<any | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const processedImageRef = useRef<string | null>(null);
  const previousModeRef = useRef<'upload' | 'camera' | null>(null);
  const [scannerKey, setScannerKey] = useState(0);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setScanError(null);
      setErrorSuggestion(null);
      setIsRetryableError(false);
      setIsProcessing(false);
      setScanResult(null);
      setShowPreview(false);
      processedImageRef.current = null;
    } else {
      // Modal opened - increment key to remount BaseScanner
      setScannerKey(prev => prev + 1);
    }
  }, [isOpen]);

  // Convert SVG data URL to PNG via canvas
  const convertSvgToPng = (svgDataUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 800;
        canvas.height = img.naturalHeight || 600;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas not supported')); return; }
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => reject(new Error('Failed to load SVG'));
      img.src = svgDataUrl;
    });
  };

  const processCard = async (imageData: string) => {
    setIsProcessing(true);
    setScanError(null);
    setErrorSuggestion(null);
    setIsRetryableError(false);
    processedImageRef.current = imageData;

    try {
      // Convert SVG to PNG on frontend before sending
      let processedImage = imageData;
      if (imageData.includes('image/svg+xml') || imageData.includes('<svg')) {
        processedImage = await convertSvgToPng(imageData);
      }

      // Call unified scan endpoint
      const token = await getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/wallet/scan-unified`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          image: processedImage.split(',')[1] || processedImage,
          imageType: 'image/png',
          autoSave: false
        })
      });

      // Check if response is ok
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Server error';
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `Server returned ${response.status}: ${errorText.substring(0, 100)}`;
        }
        
        setScanError(errorMessage);
        // Network/server errors are retryable
        setIsRetryableError(true);
        setIsProcessing(false);
        return;
      }

      // Parse JSON response
      const responseText = await response.text();
      if (!responseText) {
        setScanError('Server returned empty response');
        setIsRetryableError(true);
        setIsProcessing(false);
        return;
      }

      const result = JSON.parse(responseText);

      if (!result.success) {
        // Enhanced error handling with suggestions
        setScanError(result.error || 'Failed to scan card');
        if (result.suggestion) {
          setErrorSuggestion(result.suggestion);
        }
        
        // Determine if error is retryable based on reason
        // Validation errors (pre-validation, data validation) should NOT be retryable
        const validationReasons = [
          'file_too_small', 'file_too_large', 'invalid_format', 
          'resolution_too_low', 'resolution_too_high', 'invalid_aspect_ratio',
          'blank_image', 'too_dark', 'low_contrast',
          'confidence_too_low', 'missing_name', 'missing_contact_info',
          'invalid_name', 'insufficient_data'
        ];
        
        const isValidationError = result.reason && validationReasons.includes(result.reason);
        
        // Only retryable if it's NOT a validation error
        setIsRetryableError(!isValidationError);
        
        // For validation errors, clear the processed image ref so they can't retry
        if (isValidationError) {
          processedImageRef.current = null;
        }
        
        setIsProcessing(false);
        return;
      }

      // Successfully scanned
      setScanResult(result);
      setShowPreview(true);
      setIsProcessing(false);

    } catch (error: any) {
      console.error('Card scanning error:', error);
      setScanError(error.message || 'Network error. Please check your connection.');
      // Network errors are retryable
      setIsRetryableError(true);
      setIsProcessing(false);
    }
  };

  const handleCapture = (imageData: string) => {
    setScanError(null);
    processCard(imageData);
  };

  const handleFileUpload = (imageData: string) => {
    setScanError(null);
    processCard(imageData);
  };

  const handleRetakeWrapper = (originalRetake: () => void) => {
    setScanError(null);
    setErrorSuggestion(null);
    setIsRetryableError(false);
    setScanResult(null);
    setShowPreview(false);
    processedImageRef.current = null;
    originalRetake();
  };

  const handleFileSelectWrapper = (originalFileSelect: () => void) => {
    setScanError(null);
    setErrorSuggestion(null);
    setIsRetryableError(false);
    setScanResult(null);
    setShowPreview(false);
    processedImageRef.current = null;
    originalFileSelect();
  };

  const handleConfirmContact = () => {
    if (scanResult && scanResult.data) {
      onScanSuccess(scanResult);
      onClose();
    }
  };

  const handleRescan = () => {
    if (processedImageRef.current) {
      processCard(processedImageRef.current);
    }
  };

  const renderContent = (props: ScannerContentProps) => {
    const { 
      error, 
      capturedImage, 
      uploadedImage, 
      mode, 
      onFileSelect, 
      onRetake
    } = props;

    // Clear errors when mode changes
    if (previousModeRef.current !== null && previousModeRef.current !== mode) {
      // Mode changed, clear error states
      if (scanError || errorSuggestion) {
        setScanError(null);
        setErrorSuggestion(null);
        setIsRetryableError(false);
        processedImageRef.current = null;
      }
    }
    previousModeRef.current = mode;

    // Show scan error or camera error
    if ((scanError || error) && !isProcessing) {
      return (
        <div className="scanner-error">
          <AlertCircle size={48} />
          <p className="error-message">{scanError || error}</p>
          {errorSuggestion && (
            <p className="error-suggestion">💡 {errorSuggestion}</p>
          )}
          
          {/* Show "Try Again" only for retryable errors (network/server issues) */}
          {/* For validation errors, always prompt for new image */}
          {scanError && isRetryableError && processedImageRef.current ? (
            <div className="error-actions">
              <button onClick={handleRescan} className="btn-retry">
                <RefreshCw size={20} />
                Please try again.
              </button>
              <button 
                onClick={mode === 'upload' ? () => handleFileSelectWrapper(onFileSelect) : () => handleRetakeWrapper(onRetake)} 
                className="btn-secondary"
              >
                {mode === 'upload' ? <Upload size={20} /> : <Camera size={20} />}
                {mode === 'upload' ? 'Choose Another Image' : 'Take New Photo'}
              </button>
            </div>
          ) : (
            <button 
              onClick={mode === 'upload' ? () => handleFileSelectWrapper(onFileSelect) : () => handleRetakeWrapper(onRetake)} 
              className="btn-retry"
            >
              {mode === 'upload' ? <Upload size={20} /> : <Camera size={20} />}
              {mode === 'upload' ? 'Choose Another Image' : 'Take New Photo'}
            </button>
          )}
        </div>
      );
    }

    // Show preview with extracted contact data
    if (showPreview && scanResult) {
      return (
        <div className="scan-result-preview">
          <div className="result-header">
            <div className="result-badge">
              <CheckCircle size={20} />
              <span>Scanned via {scanResult.source === 'qr' ? 'QR Code' : 'OCR'}</span>
            </div>
            <div className="result-confidence">
              Confidence: {Math.round(scanResult.confidence * 100)}%
            </div>
          </div>

          <div className="contact-preview-card">
            <h3>{scanResult.data.displayName || 'Unknown'}</h3>
            {scanResult.data.jobTitle && (
              <p className="job-title">{scanResult.data.jobTitle}</p>
            )}
            {scanResult.data.companyName && (
              <p className="company">{scanResult.data.companyName}</p>
            )}
            
            {scanResult.data.emails && scanResult.data.emails.length > 0 && (
              <div className="contact-field">
                <strong>Email:</strong> {scanResult.data.emails[0].email}
              </div>
            )}
            
            {scanResult.data.phones && scanResult.data.phones.length > 0 && (
              <div className="contact-field">
                <strong>Phone:</strong> {scanResult.data.phones[0].number}
              </div>
            )}

            {scanResult.data.website && (
              <div className="contact-field">
                <strong>Website:</strong> {scanResult.data.website}
              </div>
            )}
          </div>

          <div className="result-meta">
            <span>Processing time: {scanResult.processingTime}ms</span>
          </div>

          <div className="preview-actions">
            <button onClick={() => handleRetakeWrapper(onRetake)} className="btn-secondary">
              <RefreshCw size={20} />
              {mode === 'upload' ? 'Upload New' : 'Retake'}
            </button>
            <button onClick={handleConfirmContact} className="btn-primary">
              <CheckCircle size={20} />
              {scannerMode === 'card' ? 'Create Card' : 'Review Contact'}
            </button>
          </div>
        </div>
      );
    }

    // Show captured/uploaded image while processing
    if ((capturedImage || uploadedImage) && !showPreview) {
      return (
        <div className="captured-image-preview">
          <img src={capturedImage || uploadedImage || ''} alt="Business card" />
          {isProcessing ? (
            <div className="processing-overlay">
              <div className="spinner"></div>
              <p>Analyzing card...</p>
              <p className="processing-detail">Detecting QR code and extracting text</p>
            </div>
          ) : null}
        </div>
      );
    }

    // Return undefined to use default BaseScanner UI
    return undefined;
  };

  return (
    <BaseScanner
      key={scannerKey}
      isOpen={isOpen}
      onClose={onClose}
      title={scannerMode === 'card' ? 'Scan My Business Card' : "Scan a Contact's Card"}
      icon={<Camera size={24} />}
      defaultMode="upload"
      frameType="card"
      onCapture={handleCapture}
      onFileUpload={handleFileUpload}
      renderContent={renderContent}
      maxFileSize={5}
      acceptedFormats="image/*"
    />
  );
};

export default BusinessCardScanner;
